import "../styling/PaymentMethodsStyles.css";
import { devLog } from "../../../utils/devLog";
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useStyles } from "../../../context/StyleContext";
import { useTranslation } from "../../../hooks/useTranslation";
import { useCheckout } from "../../../context/CheckoutContext";
import {
  mapStylesToSmartFields,
  getCountryFromCurrency,
  getLocaleFromCurrency,
} from "../../../utils/dlocalStyleMapper";
import { useDlocalSmartFields } from "../../../hooks/useDlocalSmartFields";
import { validateBrazilianDocument } from "../../../utils/validationUtils";
import { toast } from "react-toastify";
import { fetchCheckoutDetails } from "../../../api/api";
import { fetchSession, updateSession } from "../../../utils/paymentSession";
import { fetchGatewayConnection, processPayment } from "../../../api/api";
import { safeRedirect } from "../../../utils/redirectUrlBuilder";
import { formatPaymentError } from "../../../utils/formatPaymentError";
import {
  CIT_OUTCOME,
  handleCitPaymentApiResponse,
  parseCitPaymentResponse,
  applyCitDeclineOrErrorUi,
} from "../../../utils/paymentOutcome";
import DlocalCardFormView from "./DlocalCardFormView";
import useWebSocket from "../../../hooks/useWebSocket";


/**
 * dLocal Card Payment Form Component using Smart Fields
 * 
 * Implements PCI-compliant card payments via dLocal Smart Fields SDK.
 * Card details are captured in a secure iframe, only token (card_id) is exposed.
 * 
 * Features:
 * - Secure card input via dLocal Smart Fields iframe
 * - PCI-DSS SAQ A compliance
 * - Real-time card brand detection
 * - Style customization matching merchant theme
 * - 3DS authentication support
 * - Automatically saves card for future payments
 * - Both hosted and embedded (session) modes
 * - Smart Fields SDK proactively loaded when card option is visible
 * - Gateway connection prefetched by parent component for instant initialization
 */


const DlocalCardForm = ({
  formData,
  itemDetails,
  billingPlatformId,
  routingEntries,
  planId,
  prefetchedGatewayConnection,
  checkoutProcessDto,
  checkoutPageConfDto,

  // Optional props for Altruon variant
  onCardDataChange,
  isEmbeddedCheckout = false,
  sessionId,
  onParentCallback
}) => {
  // State
  const [cardholderName, setCardholderName] = useState("");
  const [documentNumber, setDocumentNumber] = useState("");
  const [documentError, setDocumentError] = useState("");
  const [pan, setPan] = useState("");
  const [panError, setPanError] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [gatewayConnection, setGatewayConnection] = useState(null);
  const [cardBrand, setCardBrand] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [transactionStatus, setTransactionStatus] = useState("");
  const [checkoutConfDto, setCheckoutConfDto] = useState(
    checkoutPageConfDto || null
  );
  const [showProcessingModal, setProcessingModal] = useState(false);
  const [transactionId, setTransactionId] = useState(null);
  const [hostedPageRedirect, setHostedPageRedirect] = useState(null);
  const [cardTouched, setCardTouched] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);

  // Ref for card element container
  const cardElementRef = useRef(null);

  // Always save card for future payments
  const saveCard = true;

  // Hooks
  const { t } = useTranslation();
  const { styles } = useStyles();
  const {
    triggerPaymentMethodEvent,
    resetTrigger,
    clientIp,
    clientCountry,
    paymentMethodName,
    platformName,
    isHostedPageMode,
  } = useCheckout();

  const locale = getLocaleFromCurrency(itemDetails?.currencyCode);
  const country = getCountryFromCurrency(itemDetails?.currencyCode);
  const isBrazilCard = country === "BR";
  const isIndiaCard = country === "IN";
  const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;

  const isSandbox =
    gatewayConnection?.credentials?.url?.includes("sandbox") || false;

  const cardDlocalRouting = useMemo(() => {
    return routingEntries?.find(
      (entry) => entry.paymentMethod === "card" && entry.platform === "dlocal"
    );
  }, [routingEntries]);

  // Initialize Smart Fields
  const {
    card,
    ready: cardReady,
    brand: detectedBrand,
    error: cardError,
    loading: sdkLoading,
    mount,
    createToken,
    clearError,
  } = useDlocalSmartFields({
    apiKey: gatewayConnection?.credentials?.smartfield_api_key,
    locale,
    country,
    isSandbox,
    styles: mapStylesToSmartFields(styles),
    onReady: () => {
      devLog("dLocal Smart Fields ready");
    },
    onError: (err) => {
      console.error("Smart Fields error:", err);
      // setError(err);
    },
    onBrandChange: (brand) => {
      devLog("Card brand detected:", brand);
      setCardBrand(brand);
    },
    onFocus: () => {
      setCardTouched(true);
    },
    onChange: () => {
      setCardTouched(true);
    },
  });

  const shouldShowCardError = (cardTouched || formSubmitted) && cardError;

  const filterRoutingEntries = (entries, paymentMethod, platform) => {
    return entries.filter(
      (entry) =>
        entry.paymentMethod === paymentMethod && entry.platform === platform
    );
  };

  useEffect(() => {
    if (cardReady && !cardError) {
      setError("");
    }
  }, [cardReady, cardError]);

  useEffect(() => {
    if (checkoutPageConfDto) {
      setCheckoutConfDto(checkoutPageConfDto);
      return;
    }
    if (billingPlatformId && planId) {
      loadCheckoutDetails();
    }
  }, [billingPlatformId, planId, checkoutPageConfDto]);

  const loadCheckoutDetails = async () => {
    try {
      const data = await fetchCheckoutDetails(billingPlatformId, planId);

      if (data?.checkoutPageConfDto) {
        setCheckoutConfDto(data.checkoutPageConfDto);
      }
    } catch (error) {
      toast.error(t("messages.error.somethingWrong") || "Something went wrong.");
    }
  };

  /**
   * Handle cardholder name change
   */
  const handleNameChange = (e) => {
    const newName = e.target.value;
    setCardholderName(newName);

    onCardDataChange?.({
      cardholderName: newName,
      saveCard: true,
    });

    if (error) {
      setError("");
    }
  };

  const handleDocumentChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    setDocumentNumber(value);
    setDocumentError("");
  };

  const handlePanChange = (e) => {
    const value = e.target.value.toUpperCase();
    setPan(value);
    setPanError("");
    onCardDataChange?.({
      cardholderName,
      pan: value,
      saveCard: true,
    });
  };

  const validatePan = (value) => {
    if (!isIndiaCard) return true;
    if (!PAN_REGEX.test(value)) {
      setPanError(t("paymentForms.upi.invalidPan"));
      return false;
    }
    return true;
  };

  const validateInputs = ({ cardholderName, cardReady, t, setError }) => {
    if (!cardholderName || cardholderName.trim().length < 3) {
      setError(t("paymentForms.card.invalidName"));
      return false;
    }
    if (!cardReady) {
      if (formSubmitted) {
        setError(t("paymentForms.card.cardNotReady"));
      }
      return false;
    }
    if (isBrazilCard && !validateBrazilianDocument(documentNumber)) {
      setDocumentError(t("paymentForms.pix.invalidDocument"));
      return false;
    }
    if (!validatePan(pan)) {
      return false;
    }
    return true;
  };


  const buildPayload = (cardToken, gatewayConnection, latestSession) => ({
    paymentData: {
      gatewayConnectionId: gatewayConnection.id,
      gatewayName: gatewayConnection.platform,
      paymentMethod: "card",
      // amount: latestSession?.paymentData?.amount,
      // currency: itemDetails?.currencyCode || latestSession?.paymentData?.currency || "UYU",
      transactionId: "",
      additionalData: {
        payment_method_flow: "DIRECT",
        cardId: cardToken,
        cardholder_name: cardholderName.trim(),
        card_brand: detectedBrand || cardBrand,
        save_card: saveCard,
        capture: true,
        verify: false,
        ...(isBrazilCard && { document: documentNumber }),
        ...(isIndiaCard && pan && { document: pan }),
      },
    },
    customerData: {
      firstName: formData?.firstName || latestSession?.customerData?.firstName,
      lastName: formData?.lastName || latestSession?.customerData?.lastName,
      email: formData?.email || latestSession?.customerData?.email,
      phone: formData?.phone || latestSession?.customerData?.phone || "9908734801",
      ip: clientIp || "127.0.0.1",
      billingAddress: {
        country: formData?.billingAddress?.country || latestSession?.customerData?.billingAddress?.country || clientCountry || "US",
        zipCode: formData?.billingAddress?.zipCode || latestSession?.customerData?.billingAddress?.zipCode || "90210",
        street: formData?.billingAddress?.street || latestSession?.customerData?.billingAddress?.street || "",
        city: formData?.billingAddress?.city || latestSession?.customerData?.billingAddress?.city || "",
        state: formData?.billingAddress?.state || latestSession?.customerData?.billingAddress?.state || "",
        number: "1234",
      },
    },
    billingData: {
      planId,
      billingPlatformId,
      billingPlatformName: itemDetails?.billingPlatformName || latestSession?.billingData?.billingPlatformName || "",
      frequency: latestSession?.billingData?.frequency || "",
    },
  });


  const handleProcessPayment = async (payload, token) => {
    if (isEmbeddedCheckout) {
      onParentCallback?.({
        type: "finalizeFormData",
        data: {
          paymentMethod: "card",
          gatewayName: "dlocal",
          cardId: token,
          cardholderName: cardholderName.trim(),
          cardBrand: detectedBrand || cardBrand,
          saveCard: true,
          ...(isIndiaCard && pan && { pan }),
        },
      });

      await new Promise((r) => setTimeout(r, 800));
    }

    if (!isEmbeddedCheckout) {
      await updateSession(sessionId, payload);
    }

    return processPayment(billingPlatformId, sessionId, payload);
  };


  /**
   * Handle payment submission
   */
  const handlePayNow = async () => {
    setFormSubmitted(true);
    if (!validateInputs({ cardholderName, cardReady, t, setError, })) return;
    if (!isEmbeddedCheckout) {
      setProcessingModal(true);
    }
    if (!cardDlocalRouting) {
      setError(t("paymentForms.card.unavailable"));
      return;
    }
    setLoading(true);
    setError("");
    clearError();

    const citUi = {
      isHostedPageMode,
      isEmbeddedCheckout,
      t,
      setError,
      setTransactionStatus,
      setShowStatusModal,
      onParentCallback,
      toast,
    };

    try {
      const cardToken = await createToken({ name: cardholderName.trim() });

      if (!cardToken) {
        throw new Error(t("paymentForms.card.tokenError"));
      }

      const gateway_connection = gatewayConnection || await fetchGatewayConnection(cardDlocalRouting, t);
      const latestSession = await fetchSession(sessionId);
      // await new Promise(resolve => setTimeout(resolve, 800));
      const payload = buildPayload(cardToken, gateway_connection, latestSession);
      if (!payload) {
        setLoading(false);
        return;
      }
      const res = await handleProcessPayment(payload);
      const { handled, parsed } = handleCitPaymentApiResponse(res, citUi);
      if (handled) return;

      if (res.status === 200) {
        if (parsed.outcome === CIT_OUTCOME.REDIRECT && parsed.redirectUrl) {
          const redirectUrl = `${parsed.redirectUrl}${parsed.redirectUrl.includes("?") ? "&" : "?"}trxId=${parsed.transactionId}`;
          setTransactionId(parsed.transactionId);
          if (isEmbeddedCheckout) {
            // Embedded SDK lives in an iframe — it can't navigate the merchant's
            // top window, so it hands the URL back to the host page to redirect.
            onParentCallback?.({ type: "actionRequired", action: "REDIRECT", url: redirectUrl });
            safeRedirect(redirectUrl, { target: "blank" });
          } else {
            // Hosted page: navigate the current tab to the gateway (3DS/APM).
            // dLocal returns the consumer to our return URL afterwards, so the
            // page resolves the final status on its own — no new tab, no
            // orphaned websocket waiting in a backgrounded tab.
            safeRedirect(redirectUrl, { target: "self" });
          }
          return;
        }
        if (res?.data?.hosted_redirect_page) {
          setHostedPageRedirect(res.data.hosted_redirect_page);
        }

        const redirectUrl = res.data.redirect_url && `${res.data.redirect_url}?trxId=${res.data.newTransactionId}`;

        setTransactionId(res?.data?.newTransactionId || res?.data?.transactionId);
        if (isEmbeddedCheckout) {
          if (redirectUrl) {
            onParentCallback?.({
              type: "actionRequired",
              action: "REDIRECT",
              url: redirectUrl,
            });
            safeRedirect(redirectUrl, { target: "blank" });
          } else {
            onParentCallback?.({
              type: "success",
              transactionId: res.data.transactionId,
            });
          }
        } else {
          setTransactionStatus("success");
          setShowStatusModal(true);
          // toast.info(t("messages.info.redirecting"));
        }
      } else {
        applyCitDeclineOrErrorUi(
          { outcome: CIT_OUTCOME.ERROR, message: t("messages.error.somethingWrong") },
          citUi
        );
        throw new Error(t("messages.error.somethingWrong"));
      }
    } catch (e) {
      if (e?.response) {
        const { handled } = handleCitPaymentApiResponse(e.response, citUi);
        if (handled) return;
      }
      applyCitDeclineOrErrorUi(
        {
          outcome: CIT_OUTCOME.ERROR,
          message: formatPaymentError(e?.response?.data?.message || e?.message, t),
        },
        citUi
      );
    } finally {
      setLoading(false);
      if (!isEmbeddedCheckout) {
        setProcessingModal(false);
      }
    }
  };

  /**
   * Use prefetched gateway connection or fetch it on mount.
   * Prefetch + this form share fetchGatewayConnection's in-flight cache, so
   * Altruon (form mounts immediately) still only hits the network once.
   */
  useEffect(() => {
    if (gatewayConnection) return;

    if (prefetchedGatewayConnection) {
      setGatewayConnection(prefetchedGatewayConnection);
      return;
    }

    const routingEntry = filterRoutingEntries(
      routingEntries,
      "card",
      "dlocal"
    );
    if (!routingEntry.length) return;

    let cancelled = false;
    fetchGatewayConnection(routingEntry[0])
      .then((connection) => {
        if (!cancelled) setGatewayConnection(connection);
      })
      .catch((error) => {
        if (!cancelled) console.error("Failed to initialize gateway:", error);
      });

    return () => {
      cancelled = true;
    };
  }, [routingEntries, prefetchedGatewayConnection, gatewayConnection]);

  /**
   * Mount Smart Fields when ready
   */
  const isDlocalCardMounted = useCallback((element) => {
    if (!element) return false;
    return Boolean(element.querySelector("iframe"));
  }, []);

  useEffect(() => {
    if (!gatewayConnection?.credentials?.smartfield_api_key) {
      return;
    }

    if (!card) {
      return;
    }

    if (!cardElementRef.current) {
      return;
    }

    if (isDlocalCardMounted(cardElementRef.current)) {
      return;
    }

    setTimeout(() => {
      try {
        if (!cardElementRef.current || isDlocalCardMounted(cardElementRef.current)) {
          return;
        }
        mount(cardElementRef.current);
      } catch (err) {
        console.error("Failed to mount Smart Fields:", err);
        setError(err.message);
      }
    }, 300);
  }, [gatewayConnection, card, mount, sdkLoading, isDlocalCardMounted]);

  /**
   * Handle payment trigger from CheckoutContext
   */
  useEffect(() => {
    if (triggerPaymentMethodEvent &&
      (isEmbeddedCheckout || (platformName === "dlocal" && paymentMethodName === "card"))) {
      handlePayNow();
      resetTrigger();
    }
  }, [
    triggerPaymentMethodEvent,
    paymentMethodName,
    platformName,
    resetTrigger,
    handlePayNow,
  ]);


  // Listen websocket only for SDK version
  useWebSocket(isEmbeddedCheckout && transactionId, (event) => {
    const data = JSON.parse(event.data); 
      
      if (!data?.status) return;

      if (data.status === "success") {
        onParentCallback?.({
          type: "success",
          transactionId,
        });
      }

      if (data.status === "failed") {
        onParentCallback?.({
          type: "failure",
        });
      }
    },
    undefined,
    undefined,
    (err) => {
      console.error("CARD WS error", err);
    }
  );

  return (
    <DlocalCardFormView
      t={t}
      cardholderName={cardholderName}
      documentNumber={documentNumber}
      documentError={documentError}
      pan={pan}
      panError={panError}
      loading={loading}
      error={error}
      cardError={shouldShowCardError ? cardError : ""}
      showCardError={cardTouched || formSubmitted}
      cardBrand={cardBrand}
      detectedBrand={detectedBrand}
      sdkLoading={sdkLoading}
      cardReady={cardReady}
      cardElementRef={cardElementRef}
      onNameChange={handleNameChange}
      onDocumentChange={handleDocumentChange}
      onPanChange={handlePanChange}
      showDocumentField={isBrazilCard}
      showPanField={isIndiaCard}
      showProcessingModal={showProcessingModal}
      setProcessingModal={setProcessingModal}
      showStatusModal={showStatusModal}
      setShowStatusModal={setShowStatusModal}
      transactionStatus={transactionStatus}
      transactionId={transactionId}
      checkoutConfDto={checkoutConfDto}
      hostedPageRedirect={hostedPageRedirect}
    />
  );
};

export default DlocalCardForm;